import type { AxiosError } from 'axios'
import type { Router } from 'vue-router'

import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'

type AuthSessionUserStore = {
    readonly loggedIn: boolean
    readonly token: string | null
    inspectToken: () => Promise<void>
    logout: () => void
}

type AuthSessionCommonStore = {
    toggleCircularLoader: (value: boolean) => void
    toggleMainLoader: (value: boolean) => void
}

type AuthSessionManagerDependencies = {
    userStore: AuthSessionUserStore
    commonStore: AuthSessionCommonStore
    router: Pick<Router, 'currentRoute' | 'replace'>
}

type AuthSessionManager = {
    handleUnauthorized: (error?: AxiosError) => void
    inspectToken: () => Promise<void>
}

export function createAuthSessionManager({ userStore, commonStore, router }: AuthSessionManagerDependencies): AuthSessionManager {
    let loginRedirectPromise: Promise<unknown> | null = null

    function getRequestToken(error?: AxiosError): string | null {
        const headers = error?.config?.headers
        const authorizationHeader = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization

        if (typeof authorizationHeader !== 'string') {
            return null
        }

        return authorizationHeader.replace(/^JWT\s+/i, '')
    }

    function redirectToLogin(): void {
        commonStore.toggleMainLoader(false)
        commonStore.toggleCircularLoader(false)

        if (router.currentRoute.value.name === 'userLogin' || loginRedirectPromise) {
            return
        }

        loginRedirectPromise = router
            .replace({ name: 'userLogin' })
            .catch(() => {})
            .finally(() => {
                loginRedirectPromise = null
            })
    }

    function handleUnauthorized(error?: AxiosError): void {
        if (!userStore.loggedIn) {
            return
        }

        const requestToken = getRequestToken(error)
        if (requestToken && requestToken !== userStore.token) {
            return
        }

        userStore.logout()
        redirectToLogin()
        notificationService.error(t('common.auth.sessionExpired'))
    }

    async function inspectToken(): Promise<void> {
        if (!userStore.loggedIn) {
            return
        }

        try {
            await userStore.inspectToken()
        } finally {
            // inspectToken may clear the session before the pending API request returns 401.
            // Redirect here so the response interceptor does not have to observe loggedIn=true.
            if (!userStore.loggedIn) {
                redirectToLogin()
            }
        }
    }

    return {
        handleUnauthorized,
        inspectToken
    }
}
